import {
    Component,
    CUSTOM_ELEMENTS_SCHEMA,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { Router } from '@angular/router';
import { lastValueFrom } from 'rxjs';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';

import { environment } from '@environment';
import { StorageService } from '@app/core/services/util/storage.service';
import { SessionStorageService } from '@app/core/services/util/session-storage.service';
import { PageNavigationService } from '@app/core/services/page-navigation/page-navigation.service';
import { GtmService } from '@core/services/analytics/gtm.service';
import { ToastrService } from '@app/core/services/notification/toastr.service';
import { OrderApiService } from '@app/core/http/order-api.service';

import { BaseComponent } from '@app/core/components/base/base.component';
import { OrderSummaryDialogComponent } from '@app/modules/manage-orders/order-summary-dialog/order-summary-dialog.component';

import { BottomStripDetail } from '@app/shared/models/bottom-strip';
import { ActionType } from '@app/shared/enums/bottom-strip';
import { OrderDetailsApiResponse } from '@app/shared/models/api-response/order-details-response';

@Component({
    selector: 'bottom-strip',
    templateUrl: './bottom-strip.component.html',
    styleUrls: ['./bottom-strip.component.scss'],
    standalone: true,
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class BottomStripComponent extends BaseComponent implements OnInit, OnChanges {
    @Input() strip: BottomStripDetail;
    @Output() showFeedback = new EventEmitter<string>();
    @Output() stripRemoved = new EventEmitter<ActionType>();
    @ViewChild('lottie', { static: false }) lottiePlayer!: ElementRef;

    DEPLOY_URL = environment.DEPLOY_URL;
    lottieUrl: string;
    // TODO: stop carousel movement till resolved fetch
    fetchInProgress: boolean;

    constructor(
        private router: Router,
        private storageService: StorageService,
        private dialog: MatDialog,
        private toastrService: ToastrService,
        private orderApiService: OrderApiService,
        private pageNavigationService: PageNavigationService,
        private gtmService: GtmService,
        private sessionStorageService: SessionStorageService
    ) {
        super();
    }

    ngOnInit() {
        this.lottieUrl = `${environment.assetsUrl}/lottie_files/${environment.brand.id}/${this.strip.header.lottie}`;
        this.checkParamsPresent();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.strip?.currentValue) {
            this.lottieUrl = `${environment.assetsUrl}/lottie_files/${environment.brand.id}/${changes.strip.currentValue.header.lottie}`;
            this.loadLottie();
        }
    }

    loadLottie() {
        try {
            this.lottiePlayer?.nativeElement?.load(this.lottieUrl);
        } catch (e) {
            // incase dotlottie sdk load failed, so load will not be available.
            console.log(e);
        }
    }

    ctaAction(event: Event) {
        const {
            action_type,
            params: { tracking_id },
            action_url
        } = this.strip.button;

        this.checkMandatoryParamsPresent();
        this.gtmService.logWebAnalyticsEvent('bottom_strip', null, `bottom_strip_click: ${action_type}`);

        switch (action_type) {
            case ActionType.Cart: {
                this.proceedToCart();
                break;
            }

            case ActionType.Feedback: {
                this.showFeedback.emit(tracking_id);
                break;
            }

            case ActionType.OrderDetails: {
                this.showOrderDetails(tracking_id);
                break;
            }

            case ActionType.OrderHistory: {
                this.pageNavigationService.navigateToManageOrders();
                break;
            }

            case ActionType.OrderPollingScreen: {
                this.router.navigateByUrl(`confirm_page?tracking_id=${tracking_id}`);
                break;
            }

            case ActionType.Tracking: {
                this.router.navigateByUrl(`live_tracking/${tracking_id}`);
                break;
            }

            case ActionType.Deeplink: {
                window.location.href = action_url;
                break;
            }

            default: {
                this.gtmService.logWebAnalyticsEvent('bottom_strip', `Invalid Action Type: ${action_type}`);
            }
        }

        event.stopPropagation();
    }

    checkParamsPresent() {
        const { text, subtext, lottie } = this.strip.header;
        const { action_type } = this.strip.button;

        if (!text || !subtext || !lottie) {
            this.gtmService.logWebAnalyticsEvent(
                'bottom_strip',
                `Missing parameters for action type: ${action_type}. text: ${text}, subtext: ${subtext}, lottie: ${lottie}`
            );
        }
    }

    checkMandatoryParamsPresent() {
        const {
            action_type,
            params: { tracking_id },
            action_url
        } = this.strip.button;

        const trackingRequiredActions = [
            ActionType.Feedback,
            ActionType.OrderDetails,
            ActionType.OrderPollingScreen,
            ActionType.Tracking
        ];

        if (trackingRequiredActions.includes(action_type) && !tracking_id) {
            this.gtmService.logWebAnalyticsEvent('bottom_strip', `Tracking ID is missing for action type: ${action_type}`);
        }

        if (action_type === ActionType.Deeplink && !action_url) {
            this.gtmService.logWebAnalyticsEvent('bottom_strip', `Action URL is missing for action type: ${action_type}`);
        }
    }

    showOrderDetails(tracking_id: number | string) {
        this.fetchInProgress = true;

        lastValueFrom(this.orderApiService.getLiveOrders(tracking_id))
            .then((res: OrderDetailsApiResponse) => {
                if (res.meta.code === 200) {
                    this.openOrderDetailsModal(res.order);
                } else {
                    this.toastrService.openSnackBar(res.meta.message, 'OKAY');
                }
            })
            .catch(err => {
                this.toastrService.openSnackBar(err?.meta?.message, 'OKAY');
            })
            .finally(() => {
                this.fetchInProgress = false;
            });
    }

    openOrderDetailsModal(order: OrderDetailsApiResponse['order']) {
        const dialogConfig = new MatDialogConfig();
        dialogConfig.panelClass = 'order-details-dialog';
        dialogConfig.data = { order };
        dialogConfig.autoFocus = false;
        this.dialog.open(OrderSummaryDialogComponent, dialogConfig);
    }

    isLoggedIn() {
        return this.storageService.get('session-id') && this.storageService.get('phone_no');
    }

    proceedToCart() {
        this.router.navigateByUrl('/cart');
    }

    disableStrip(e: Event) {
        const {
            action_type,
            params: { tracking_id }
        } = this.strip.button;
        if (action_type === ActionType.Feedback) {
            this.sessionStorageService.set('feedback-strip-tracking-id', tracking_id);
        }

        this.stripRemoved.emit(action_type);
        e.stopPropagation();
    }
}
